import { lazy } from 'react';

const RELOADED_FLAG = 'lazy-chunk-error-reloaded';

/**
 * Drop-in replacement for React.lazy that recovers from ChunkLoadError.
 *
 * A lazy chunk request can fail for two reasons:
 *  1. a transient network blip -> a simple retry fixes it (webpack re-requests a
 *     failed chunk on the next import() call)
 *  2. the user's open tab references a build that a redeploy has replaced, so the
 *     hashed chunk file no longer exists -> only a full reload (fetching the new
 *     index.html and runtime) can fix it
 *
 * So: retry the import once; if it still fails, reload the page once. The
 * sessionStorage flag guarantees a genuinely broken deploy can't cause a reload
 * loop - the second failure after a reload surfaces the error to the nearest
 * error boundary instead.
 *
 */
export const lazyWithRetry = importFn =>
  lazy(async () => {
    try {
      const module = await importFn();
      window.sessionStorage.removeItem(RELOADED_FLAG);
      return module;
    } catch (error) {
      try {
        const module = await importFn();
        window.sessionStorage.removeItem(RELOADED_FLAG);
        return module;
      } catch (retryError) {
        if (!window.sessionStorage.getItem(RELOADED_FLAG)) {
          window.sessionStorage.setItem(RELOADED_FLAG, 'true');
          window.location.reload();
          // never resolves - the page is reloading
          return new Promise(() => {});
        }
        throw retryError;
      }
    }
  });
